import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { MapPin, Calendar, Users, Package, LogOut, ChevronRight } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TrekkerPortal() {
  const { user, logout } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/trekker/dashboard`, {
          credentials: "include",
        });
        if (res.ok) {
          const data = await res.json();
          setBookings(data.bookings || []);
        }
      } catch (err) {
        console.error("Failed to load dashboard:", err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const statusColor = {
    confirmed: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
    pending: "bg-amber-500/20 text-amber-400 border-amber-500/30",
    completed: "bg-blue-500/20 text-blue-400 border-blue-500/30",
    cancelled: "bg-red-500/20 text-red-400 border-red-500/30",
  };

  return (
    <div className="min-h-screen bg-stone-950 pt-20 pb-16" data-testid="trekker-portal">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-10">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-white tracking-tight" data-testid="portal-heading">
              Trekker Portal
            </h1>
            <p className="text-stone-400 mt-1">
              Welcome back, <span className="text-amber-400">{user?.name}</span>
            </p>
          </div>
          <Button
            onClick={logout}
            variant="outline"
            className="border-stone-700 text-stone-300 hover:bg-stone-800 rounded-xl self-start"
            data-testid="portal-logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          {[
            { label: "Total Treks", value: bookings.length, icon: MapPin },
            { label: "Upcoming", value: bookings.filter(b => b.status === "confirmed").length, icon: Calendar },
            { label: "Completed", value: bookings.filter(b => b.status === "completed").length, icon: Package },
            { label: "Participants", value: bookings.reduce((acc, b) => acc + (b.participants?.length || 0), 0), icon: Users },
          ].map((stat) => (
            <div
              key={stat.label}
              className="bg-stone-900/60 border border-stone-800 rounded-xl p-4"
              data-testid={`stat-${stat.label.toLowerCase().replace(/\s/g, "-")}`}
            >
              <stat.icon className="w-5 h-5 text-amber-500 mb-2" />
              <p className="text-2xl font-bold text-white">{stat.value}</p>
              <p className="text-stone-400 text-sm">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Bookings */}
        <h2 className="text-xl font-semibold text-white mb-4">My Treks</h2>
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-500" />
          </div>
        ) : bookings.length === 0 ? (
          <div className="bg-stone-900/60 border border-stone-800 rounded-2xl p-12 text-center" data-testid="no-bookings">
            <MapPin className="w-12 h-12 text-stone-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No treks booked yet</h3>
            <p className="text-stone-400 mb-6">
              Your upcoming and past treks will appear here once the admin assigns them to you.
            </p>
            <Link to="/build-adventure">
              <Button className="bg-amber-600 hover:bg-amber-700 text-white rounded-xl" data-testid="explore-treks-btn">
                Explore Adventures
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {bookings.map((booking) => (
              <Link
                to={`/portal/booking/${booking.booking_id}`}
                key={booking.booking_id}
                className="block"
                data-testid={`booking-card-${booking.booking_id}`}
              >
                <div className="bg-stone-900/60 border border-stone-800 rounded-xl p-5 hover:border-amber-500/30 transition-all group">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="text-lg font-semibold text-white group-hover:text-amber-400 transition-colors">
                          {booking.program_name || "Trek Program"}
                        </h3>
                        <Badge className={`text-xs ${statusColor[booking.status] || statusColor.pending}`}>
                          {booking.status}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap gap-4 text-stone-400 text-sm">
                        {booking.departure_date && (
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5" />
                            {booking.departure_date}
                          </span>
                        )}
                        {booking.participants?.length > 0 && (
                          <span className="flex items-center gap-1">
                            <Users className="w-3.5 h-3.5" />
                            {booking.participants.length} participant{booking.participants.length !== 1 ? "s" : ""}
                          </span>
                        )}
                        {booking.total_amount > 0 && (
                          <span className="text-amber-400 font-medium">
                            USD ${booking.total_amount.toLocaleString()}
                          </span>
                        )}
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-stone-500 group-hover:text-amber-400 transition-colors hidden sm:block" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
